import type React from 'react';

import { useI18nContext } from '@/i18n/i18n-react';

import { SidebarLayout } from '@/layouts/sidebar.layout';
import { useNavigate, useParams } from 'react-router'; // Reverted to react-router

import { useTenant } from '@/hooks/use-tenant';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useMutation } from '@tanstack/react-query';
import { cancelShopifySubscription } from '@/lib/api-client';
import { Loader } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { LL } = useI18nContext();
  const tenantSlug = useParams().tenantSlug ?? '';
  const tenant = useTenant();

  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] =
    useState(false);

  const { isPending, isSuccess, mutate } = useMutation({
    mutationFn: () => cancelShopifySubscription(tenantSlug),
    onSuccess: () => {
      console.log('aqui');
      setIsCancelConfirmationModalOpen(false);
    },
  });

  const handlePlanCancellation = () => {
    mutate();
  };

  useEffect(() => {
    if (!tenant.fromShopifyAppStore) {
      navigate(`/${tenantSlug}/dashboard`);
    }
  }, [navigate, tenant.fromShopifyAppStore, tenantSlug]);

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {LL.settingsPage.title()}
          </h1>
        </div>

        {tenant.fromShopifyAppStore && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-bold">
                {LL.settingsPage.activePlan()}
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {tenant.shopifySubscriptionStatus === 'ACTIVE' ? (
                <>
                  <p className="text-muted-foreground">
                    {LL.settingsPage.korzaOneMonthly()} - $60
                  </p>
                  <Dialog
                    open={isCancelConfirmationModalOpen}
                    onOpenChange={setIsCancelConfirmationModalOpen}
                  >
                    <DialogTrigger asChild>
                      <Button
                        className="max-w-36 border-destructive text-red-700 hover:bg-destructive/50 hover:text-red-700"
                        variant="outline"
                      >
                        {LL.settingsPage.cancelPlan()}
                      </Button>
                    </DialogTrigger>

                    <DialogContent>
                      <div className="shake flex flex-col gap-4">
                        <DialogHeader>
                          <DialogTitle>
                            {LL.settingsPage.cancelConfirmationDialogTitle()}
                          </DialogTitle>
                          <DialogDescription>
                            {LL.settingsPage.cancelConfirmationDialogDescription()}
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {LL.settingsPage.goBack()}
                            </Button>
                          </DialogClose>
                          <Button
                            className="max-w-36 border-destructive text-red-700 hover:bg-destructive/50 hover:text-red-700"
                            variant="outline"
                            onClick={handlePlanCancellation}
                            disabled={isPending || isSuccess}
                          >
                            {isPending ? (
                              <Loader />
                            ) : (
                              LL.settingsPage.cancelPlan()
                            )}
                          </Button>
                        </DialogFooter>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <p className="text-muted-foreground">
                  {LL.settingsPage.noPlanActive()}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </SidebarLayout>
  );
}
